/**
 * @fileoverview 首页 - 市场大厅
 * @description 展示平台核心信息：Hero 区域、价格趋势预测市场（高频/5分钟竞猜）、热点事件预测市场（普通市场）
 * 已接入后端 API：从 useMarketStore 获取真实市场数据
 * 设计风格：币安深黑配色，Hero 区域使用高清背景图，非对称布局
 *
 * 市场分类：
 *   - 价格趋势预测市场 = marketType=high_freq（5分钟价格走势竞猜）
 *   - 热点事件预测市场 = marketType=normal（普通市场）
 *
 * 市场排序规则（两个分类均适用）：
 *   1. 用户参与中且未出结果的市场（status=0 的 investRecord 对应的 marketId）优先
 *   2. 其余活跃市场按流动性（totalLiquidity）从多到少
 *   3. 休市（status=closed）的市场排最后
 *
 * @module pages/Home
 */
import { ArrowRight, BarChart3, Clock, TrendingUp, Users, Zap, LayoutGrid } from 'lucide-react';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import MarketCard from '../components/market/MarketCard';
import HighFreqMarketCard from '../components/market/HighFreqMarketCard';
import MainLayout from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { useMarketStore } from '../stores/useMarketStore';
import { useRecentMarkets } from '../hooks/useRecentMarkets';
import { useAppStore } from '../stores/useAppStore';
import { fetchInvestList } from '../services/userService';
import { API_OK } from '../services/api';
import { formatLargeNumber } from '../lib/utils';
import type { Market } from '../types';

// 高清 Hero 背景图（2752×1536px）
const HERO_BG = '/bg/hero-bg.webp';

// ============================================================
// 工具函数：新排序规则
// ============================================================
/**
 * 对市场列表进行个性化排序
 * 规则：
 *   ① 用户参与中且未出结果的市场（activeMarketIds）优先，按流动性降序
 *   ② 其余活跃市场按流动性（totalLiquidity）降序
 *   ③ 休市（status=closed）排最后，按流动性降序
 */
function sortMarkets(markets: Market[], activeMarketIds: Set<string>): Market[] {
  const active: Market[] = [];
  const normal: Market[] = [];
  const closed: Market[] = [];

  for (const m of markets) {
    if (m.status === 'closed') {
      closed.push(m);
    } else if (activeMarketIds.has(m.id)) {
      active.push(m);
    } else {
      normal.push(m);
    }
  }

  const byLiquidityDesc = (a: Market, b: Market) =>
    (b.totalLiquidity ?? 0) - (a.totalLiquidity ?? 0);

  return [
    ...active.sort(byLiquidityDesc),
    ...normal.sort(byLiquidityDesc),
    ...closed.sort(byLiquidityDesc),
  ];
}

// ============================================================
// 子组件：Hero 区域
// ============================================================
function HeroSection({ marketCount }: { marketCount: number }) {
  const { t } = useTranslation();
  const stats = [
    { label: t('home.hero.stats.totalVolume'), value: '$' + formatLargeNumber(485000000), icon: BarChart3, color: '#F0B90B' },
    { label: t('home.hero.stats.activeMarkets'), value: String(marketCount || 128), icon: LayoutGrid, color: '#0ECB81' },
    { label: t('home.hero.stats.totalUsers'), value: formatLargeNumber(92000) + '+', icon: Users, color: '#3B82F6' },
  ];
  return (
    <div
      className="relative overflow-hidden"
      style={{
        backgroundImage: `url(${HERO_BG})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
        minHeight: '520px',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E11]/97 via-[#0B0E11]/85 to-[#0B0E11]/40" />
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0B0E11] to-transparent" />
      <div className="relative container py-16 md:py-24">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-brand/10 border border-brand/25 rounded-full px-4 py-1.5 mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            <span className="text-brand text-xs font-semibold tracking-wider uppercase">
              {t('home.hero.badge')}
            </span>
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '-0.02em' }}
          >
            {t('home.hero.titleLine1')}<br />
            <span style={{ color: '#F0B90B' }}>{t('home.hero.titleLine2')}</span>
          </h1>
          <p className="text-white/60 text-lg mb-8 max-w-lg leading-relaxed">
            {t('home.hero.subtitle')}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link href="/markets">
              <Button className="btn-brand gap-2 text-base px-6 py-3 h-auto">
                {t('home.hero.cta')}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Link href="/leaderboard">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 text-base px-6 py-3 h-auto">
                {t('home.hero.leaderboardCta')}
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-6 mt-10">
            {stats.map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ background: `${color}15` }}>
                  <Icon className="h-5 w-5" style={{ color }} />
                </div>
                <div>
                  <p className="text-xl font-bold text-white font-mono">{value}</p>
                  <p className="text-xs text-white/50">{label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// 子组件：价格趋势预测市场（原高频市场，5分钟竞猜）
// ============================================================
function PriceTrendSection({ markets, activeMarketIds }: { markets: Market[]; activeMarketIds: Set<string> }) {
  const { t } = useTranslation();
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{
              background: 'linear-gradient(135deg, rgba(240,185,11,0.15) 0%, rgba(240,185,11,0.05) 100%)',
              border: '1px solid rgba(240,185,11,0.3)',
            }}
          >
            <TrendingUp className="h-4 w-4 text-brand" />
            <span className="text-brand text-sm font-bold tracking-wide">{t('home.sections.priceTrend')}</span>
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(240,185,11,0.2)', color: '#F0B90B' }}
            >
              5MIN
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">{t('home.highFreq.desc')}</span>
          </div>
        </div>
        <Link
          href="/markets?category=high_freq"
          className="flex items-center gap-1 text-sm text-brand hover:text-brand/80 transition-colors font-medium"
        >
          {t('home.viewAll')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {markets.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {markets.map((market, index) => (
            <HighFreqMarketCard key={market.id} market={market} animationDelay={index * 50} isParticipating={activeMarketIds.has(market.id)} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-48 rounded-xl bg-surface-2 animate-pulse" />
          ))}
        </div>
      )}
    </section>
  );
}

// ============================================================
// 子组件：热点事件预测市场（普通市场）
// ============================================================
interface HotEventSectionProps {
  markets: Market[];
  loading?: boolean;
  activeMarketIds: Set<string>;
}
function HotEventSection({ markets, loading = false, activeMarketIds }: HotEventSectionProps) {
  const { t } = useTranslation();
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
            style={{
              background: 'linear-gradient(135deg, rgba(14,203,129,0.12) 0%, rgba(14,203,129,0.04) 100%)',
              border: '1px solid rgba(14,203,129,0.25)',
            }}
          >
            <Zap className="h-4 w-4" style={{ color: '#0ECB81' }} />
            <span className="text-sm font-bold tracking-wide" style={{ color: '#0ECB81' }}>
              {t('home.sections.hotEvent')}
            </span>
          </div>
        </div>
        <Link
          href="/markets"
          className="flex items-center gap-1 text-sm text-brand hover:text-brand/80 transition-colors font-medium"
        >
          {t('home.viewAll')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-48 rounded-xl bg-surface-2 animate-pulse" />
          ))}
        </div>
      ) : markets.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {markets.map((market, index) => (
            <MarketCard key={market.id} market={market} animationDelay={index * 50} isParticipating={activeMarketIds.has(market.id)} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

// ============================================================
// 主页面组件
// ============================================================
export default function Home() {
  const { markets, fetchMarkets } = useMarketStore();
  const { isAuthenticated } = useAppStore();
  const { recentIds } = useRecentMarkets(markets);

  // 用户参与中且未出结果的市场 ID 集合
  const [activeMarketIds, setActiveMarketIds] = useState<Set<string>>(new Set());

  // 页面加载时拉取市场数据
  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  // 高频市场自动轮询：每 15 秒刷新一次
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const hasHighFreq = markets.some((m) => m.marketType === 'high_freq');
    if (!hasHighFreq) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      fetchMarkets();
    }, 15_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [markets, fetchMarkets]);

  // 拉取用户参与中（status=0）的订单，提取 marketId 集合
  const loadActiveMarkets = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const resp = await fetchInvestList(0);
      if (resp.status === API_OK && Array.isArray(resp.data)) {
        const ids = new Set(
          resp.data
            .filter((r) => r.status === 0)  // status=0 表示投注中（未出结果）
            .map((r) => String(r.marketId))
        );
        setActiveMarketIds(ids);
      }
    } catch {
      // 未登录或请求失败时静默处理，不影响首页展示
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadActiveMarkets();
  }, [loadActiveMarkets]);

  // ── 数据派生 ──────────────────────────────────────────────
  const isLoading = markets.length === 0;

  // 价格趋势预测市场 = 高频市场（5分钟竞猜）
  // 排序：用户参与中未出结果 → 流动性降序 → 休市
  const priceTrendMarkets = sortMarkets(
    markets.filter((m) => m.marketType === 'high_freq'),
    activeMarketIds
  );

  // 热点事件预测市场 = 普通市场（非高频）
  // 排序：用户参与中未出结果 → 流动性降序 → 休市，最多展示 8 个
  const hotEventMarkets = sortMarkets(
    markets.filter((m) => m.marketType !== 'high_freq'),
    activeMarketIds
  ).slice(0, 8);

  return (
    <MainLayout>
      {/* Hero 区域 */}
      <HeroSection marketCount={markets.filter((m) => m.status === 'active').length} />

      {/* 主内容区 */}
      <div className="container py-8 space-y-10">

        {/* 价格趋势预测市场（原高频市场，5分钟竞猜） */}
        <PriceTrendSection markets={priceTrendMarkets} activeMarketIds={activeMarketIds} />

        {/* 热点事件预测市场（普通市场） */}
        <HotEventSection markets={hotEventMarkets} loading={isLoading} activeMarketIds={activeMarketIds} />

      </div>
    </MainLayout>
  );
}
